import { gql } from '@apollo/client';
import React from 'react';
import GroupMention from '../editor/mentions/GroupMention';
import PostMention from '../editor/mentions/PostMention';
import TopicMention from '../editor/mentions/TopicMention';
import UserMention from '../editor/mentions/UserMention';
import type { PostInsightsBacklinks_PostFragment } from '../graphql/generated/graphqlTypes';
import { PostType } from '../graphql/generated/graphqlTypes';
import styles from './PostInsightsBacklinks.module.scss';

const POST_FRAGMENT = gql`
  fragment PostInsightsBacklinks_post on Post {
    id
    backlinks {
      ... on Post {
        id
        title
        type
        archivedAt
      }
      ... on User {
        id
        name
        deactivatedAt
      }
      ... on Group {
        id
        name
      }
      ... on Topic {
        id
        name
      }
    }
  }
`;

interface PostInsightsBacklinksProps {
  post: PostInsightsBacklinks_PostFragment;
  showHoverCard?: boolean;
  newTab?: boolean;
}

const ORDER = [
  'Post',
  'Topic',
  'User',
  'Group',
  'Template',
  'ArchivedPost',
  'ArchivedTemplate',
  'DeactivatedUser',
  '',
] as const;

const getType = (
  backlink: PostInsightsBacklinks_PostFragment['backlinks'][number],
): typeof ORDER[number] => {
  if (backlink.__typename === 'Post') {
    return `${backlink.archivedAt ? 'Archived' : ''}${
      backlink.type === PostType.Post ? 'Post' : 'Template'
    }`;
  }
  if (backlink.__typename === 'User' && backlink.deactivatedAt) {
    return 'DeactivatedUser';
  }
  return backlink.__typename ?? '';
};

const PostInsightsBacklinks = ({
  post,
  showHoverCard,
  newTab,
}: PostInsightsBacklinksProps) => {
  const backlinks = [...(post.backlinks ?? [])].sort(
    (a, b) => ORDER.indexOf(getType(a)) - ORDER.indexOf(getType(b)),
  );

  return (
    <div className={styles.backlinks}>
      {backlinks.map((backlink) => (
        <div key={backlink.id} className={styles.backlink}>
          {backlink.__typename === 'Post' && (
            <PostMention
              id={backlink.id}
              content={backlink.title}
              link
              singleLine
              showHoverCard={showHoverCard}
              newTab={newTab}
            />
          )}
          {backlink.__typename === 'Topic' && (
            <TopicMention
              id={backlink.id}
              content={backlink.name}
              link
              singleLine
              showHoverCard={showHoverCard}
              newTab={newTab}
            />
          )}
          {backlink.__typename === 'User' && (
            <UserMention
              id={backlink.id}
              content={backlink.name}
              link
              singleLine
              showHoverCard={showHoverCard}
              newTab={newTab}
            />
          )}
          {backlink.__typename === 'Group' && (
            <GroupMention
              id={backlink.id}
              content={backlink.name}
              link
              singleLine
              showHoverCard={showHoverCard}
              newTab={newTab}
            />
          )}
        </div>
      ))}
    </div>
  );
};
PostInsightsBacklinks.fragments = {
  post: POST_FRAGMENT,
};

export default PostInsightsBacklinks;
